import { css } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router';
import { BrokerDealer, Industry, TemplateCategoryType } from '../../../../../extViewmodels';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { Modal } from '../../../../../web/components/Modal';
import { ISelectBoxOption, SelectBox } from '../../../../../web/components/SelectBox';
import { TextInput } from '../../../../../web/components/TextInput';
import { bs } from '../../../../../web/styles/styles';
import { getBrokerDealerOptions } from '../../../../containers/account/getBrokerDealerOptions';
import { styleSheet } from '../styles';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onCreateClick: (categoryName: string, type: TemplateCategoryType) => void;
}

export function CampaignLibraryNewCategoryModal({ isOpen, onClose, onCreateClick }: Props) {
	const [value, setValue] = React.useState('');
	const [createCategoryForBD, setCreateCategoryForBD] = React.useState<boolean>(false);
	const [selectedBrokerDealer, setSelectedBrokerDealer] = React.useState<BrokerDealer | null>(null);
	const match = useRouteMatch<{ category: string }>('/campaigns/:category');

	const industryFromUrl = React.useMemo(() => {
		const category = match.params.category || '';
		const matchedIndustry = Object.values(Industry).find(ind => ind.toLowerCase() === category.toLowerCase());
		return matchedIndustry;
	}, [match.params]);

	const [industry] = React.useState<Industry>(industryFromUrl);
	const isFinancialIndustry = React.useMemo(() => industry === Industry.Financial, [industry]);

	const brokerDealerOptions = React.useMemo(() => getBrokerDealerOptions(industry), [industry]);

	const onBrokerDealerChanged = (option: ISelectBoxOption<BrokerDealer>) => {
		setSelectedBrokerDealer(option.value);
		setValue(option.value);
	};

	const getBrokerDealerSelection = (): ISelectBoxOption<BrokerDealer> | null => {
		if (!selectedBrokerDealer) {
			return null;
		}
		return {
			title: selectedBrokerDealer,
			value: selectedBrokerDealer,
		};
	};

	const handleClose = () => {
		setValue('');
		setCreateCategoryForBD(false);
		setSelectedBrokerDealer(null);
		onClose();
	};

	const handleCreate = () => {
		const categoryType = createCategoryForBD ? TemplateCategoryType.BrokerDealer : TemplateCategoryType.Default;
		onCreateClick(value, categoryType);
		setValue('');
		setCreateCategoryForBD(false);
		setSelectedBrokerDealer(null);
	};
	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			<div className={css(bs.textBrandSecondary, bs.textLg)}>Add New Category</div>
			<div className={css(bs.textTitles, bs.textSm, bs.mt2, bs.mb1)}>Category Name</div>
			<TextInput
				inputId='new-category-input'
				onChange={ev => setValue(ev.target.value)}
				type='text'
				value={value}
				disabled={createCategoryForBD}
			/>

			{isFinancialIndustry && (
				<div className={css(bs.mt3)}>
					<Checkbox
						checked={createCategoryForBD}
						onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setCreateCategoryForBD(ev.target.checked)}
						label='Create Category for Broker Dealer'
					>
						<span>Create Category for Broker Dealer</span>
					</Checkbox>
				</div>
			)}

			{isFinancialIndustry && createCategoryForBD && (
				<div className={css(bs.mt3)}>
					<div className={css(bs.textTitles, bs.textSm, bs.mb1)}>Broker Dealer</div>
					<SelectBox
						contentClassName={`${css(styleSheet.dropdownMenu)}`}
						onSelectionChanged={onBrokerDealerChanged}
						options={brokerDealerOptions || []}
						selectedOption={getBrokerDealerSelection()}
						triggerClassName={`${css(styleSheet.dropdownTrigger)}`}
					/>
				</div>
			)}

			<div className={css(bs.mt5, bs.flex, bs.itemsCenter, bs.gap5)}>
				<button className={css(bs.ctaButton)} onClick={handleCreate}>
					<span>Create</span>
				</button>
				<button className={css(bs.ctaButtonReverse)} onClick={handleClose}>
					<span>Cancel</span>
				</button>
			</div>
		</Modal>
	);
}
